import { toast } from "sonner";

type ToastAction = { label: string; onClick: () => void };

interface FarmlyToastOpts {
  description?: string;
  action?: ToastAction;
  duration?: number;
}

const DEFAULTS = { duration: 3500, dismissible: true };

/**
 * Create a Sonner toast with click-to-dismiss.
 * Uses a two-step approach: create toast to get ID, then update with onDismiss bound.
 */
function withClickDismiss(
  createFn: typeof toast,
  message: string,
  opts?: FarmlyToastOpts,
) {
  // First create to get the id
  const id = createFn(message, { ...DEFAULTS, ...opts });
  // Re-create with same id — Sonner merges/updates in place
  createFn(message, {
    ...DEFAULTS,
    ...opts,
    id,
    // Sonner doesn't have onClick, so we use onDismiss for tracking.
    // Actual click-to-dismiss is handled by installClickToDismiss().
  });
  return id;
}

/**
 * Farmly-standard toast with click-to-dismiss.
 */
export function farmlyToast(message: string, opts?: FarmlyToastOpts) {
  return toast(message, { ...DEFAULTS, ...opts });
}

export function farmlyToastSuccess(message: string, opts?: FarmlyToastOpts) {
  return toast.success(message, { ...DEFAULTS, ...opts });
}

export function farmlyToastError(message: string, opts?: FarmlyToastOpts) {
  return toast.error(message, { ...DEFAULTS, ...opts });
}

/**
 * Global click-to-dismiss for ALL Sonner toasts.
 * Clicking anywhere on a toast (except action/cancel buttons) dismisses it.
 * Call once at app startup.
 *
 * Uses MutationObserver to stamp each toast element with its Sonner ID
 * so we can reliably dismiss individual toasts on click.
 */
let installed = false;
export function installClickToDismiss() {
  if (installed || typeof document === "undefined") return;
  installed = true;

  // Stamp toast elements with their IDs using MutationObserver
  const stampToasts = () => {
    const activeToasts = toast.getToasts();
    const toasterEl = document.querySelector("[data-sonner-toaster]");
    if (!toasterEl) return;

    const toastEls = toasterEl.querySelectorAll("[data-sonner-toast]");
    // Sonner renders toasts in order matching getToasts()
    toastEls.forEach((el, i) => {
      if (i < activeToasts.length && !("dismiss" in activeToasts[i])) {
        (el as HTMLElement).dataset.farmlyToastId = String(activeToasts[i].id);
      }
    });
  };

  // Observe for new toasts being added
  const observer = new MutationObserver(() => {
    requestAnimationFrame(stampToasts);
  });

  // Start observing once toaster appears
  const waitForToaster = () => {
    const toaster = document.querySelector("[data-sonner-toaster]");
    if (toaster) {
      observer.observe(toaster, { childList: true, subtree: true });
    } else {
      requestAnimationFrame(waitForToaster);
    }
  };
  // Delay to let React render
  setTimeout(waitForToaster, 100);

  // Click handler
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;

    // Don't dismiss when clicking action/cancel buttons
    if (target.closest("[data-button]") || target.closest("[data-cancel]")) return;

    const toastEl = target.closest("[data-sonner-toast]") as HTMLElement | null;
    if (!toastEl) return;

    // Try stamped ID first
    const stampedId = toastEl.dataset.farmlyToastId;
    if (stampedId) {
      const numId = Number(stampedId);
      toast.dismiss(isNaN(numId) ? stampedId : numId);
      return;
    }

    // Fallback: dismiss the single active toast if only one
    const active = toast.getToasts().filter((t) => !("dismiss" in t));
    if (active.length === 1) {
      toast.dismiss(active[0].id);
    }
  });
}
